import { Ban, type LucideIcon } from "lucide-react";
import { FormProvider } from "react-hook-form";

import { inputCls } from "../../../components";
import { flattenAdvisoryAgainst } from "./AdvisoryAgainstForm.rules";
import { AdvisoryAgainstFormProps } from "./AdvisoryAgainstForm.types";
import { useAdvisoryAgainstForm } from "./hooks/useAdvisoryAgainstForm";
import { cn } from "@/lib/utils";

/** Visual section card with icon + title header. */
function FormCard({
  icon: Icon,
  title,
  description,
  className,
  children,
}: {
  icon: LucideIcon;
  title: string;
  description?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <section
      className={cn(
        "rounded-xl border border-gray-200 dark:border-gray-700/70",
        "bg-white/60 dark:bg-gray-900/40 backdrop-blur-[1px]",
        "shadow-sm",
        className
      )}
    >
      <header className="flex items-start gap-3 px-4 sm:px-5 pt-4 pb-3 border-b border-gray-100 dark:border-gray-800">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-rose-50 text-rose-600 dark:bg-rose-950/50 dark:text-rose-400">
          <Icon className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 leading-tight">
            {title}
          </h3>
          {description && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {description}
            </p>
          )}
        </div>
      </header>
      <div className="px-4 sm:px-5 py-4">{children}</div>
    </section>
  );
}

export function AdvisoryAgainstForm({
  onDocumentCreated,
  onClose,
}: AdvisoryAgainstFormProps) {
  const form = useAdvisoryAgainstForm();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = form;

  const onSubmit = (data: Parameters<typeof flattenAdvisoryAgainst>[0]) => {
    onDocumentCreated?.(flattenAdvisoryAgainst(data));
    onClose?.();
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <FormCard
          icon={Ban}
          title="Advisory against"
          description="Meeting information used to justify advising against the product."
        >
          <div className="space-y-4">
            <div className="text-sm font-semibold text-gray-800 dark:text-gray-200">
              Meeting information:
            </div>

            <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-200">
              <input
                type="checkbox"
                {...register("docRejectReason1")}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span>
                Advising against is based on the information we have about
                knowledge and experience.
              </span>
            </label>

            <div>
              <label className="block mb-1.5 text-xs font-medium text-gray-600 dark:text-gray-300">
                Specify which product / service / description of the advisory:
              </label>
              <textarea
                {...register("docRejectDet")}
                rows={10}
                className={`${inputCls} resize-y`}
              />
            </div>

            <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-200">
              <input
                type="checkbox"
                {...register("docRejectReason2")}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span>
                Due to missing information we are unable to assess whether the
                product is suitable.
              </span>
            </label>
          </div>
        </FormCard>

        {Object.keys(errors).length > 0 && (
          <p className="text-xs text-red-500">
            Please fill in all required fields.
          </p>
        )}

        {/* Footer actions — sticky to dialog bottom */}
        <div className="sticky bottom-0 -mx-4 px-4 py-3 mt-2 border-t border-gray-200 dark:border-gray-700 bg-white/95 dark:bg-gray-900/95 backdrop-blur supports-[backdrop-filter]:bg-white/80 dark:supports-[backdrop-filter]:bg-gray-900/80 flex justify-end gap-3 z-10">
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            className="px-5 py-2 rounded-md bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white text-sm font-semibold shadow-sm transition-colors"
          >
            Add to document
          </button>
        </div>
      </form>
    </FormProvider>
  );
}
